import { z } from 'zod';
import { levelSchema, topicSchema } from './topics';

/*
 * The learning catalog: every published course and quiz a learner can take,
 * searchable and filterable (`GET /v1/catalog`).
 */

/** What kind of thing a catalog item is. */
export const CATALOG_TYPES = [
  'course',
  'self_assessment',
  'assessment',
  'workshop',
  'webinar',
] as const;
export type CatalogType = (typeof CATALOG_TYPES)[number];
export const CATALOG_TYPE_LABELS: Record<CatalogType, string> = {
  course: 'Course',
  self_assessment: 'Self-assessment',
  assessment: 'Assessment',
  workshop: 'Workshop',
  webinar: 'Webinar',
};

export const CATALOG_LENGTHS = {
  under_15: { label: 'Under 15 minutes', min: 0, max: 14 },
  '15_60': { label: '15 to 60 minutes', min: 15, max: 60 },
  '1_3h': { label: '1 to 3 hours', min: 61, max: 180 },
  over_3h: { label: 'Over 3 hours', min: 181, max: null },
} as const;
export type CatalogLength = keyof typeof CATALOG_LENGTHS;

export const CATALOG_STATUSES = ['not_started', 'in_progress', 'completed'] as const;
export type CatalogStatus = (typeof CATALOG_STATUSES)[number];
export const CATALOG_STATUS_LABELS: Record<CatalogStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
};

/** `soonest`: events by their first session. */
export const CATALOG_SORTS = ['relevance', 'newest', 'popular', 'soonest'] as const;
export type CatalogSort = (typeof CATALOG_SORTS)[number];

export const CATALOG_PAGE_SIZE = 20;

export const catalogQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  type: z.enum(CATALOG_TYPES).optional(),
  /** `nirvik` for Nirvik's library, or an organization id. */
  source: z.union([z.literal('nirvik'), z.string().uuid()]).optional(),
  topic: topicSchema.optional(),
  level: levelSchema.optional(),
  length: z.enum(Object.keys(CATALOG_LENGTHS) as [CatalogLength, ...CatalogLength[]]).optional(),
  status: z.enum(CATALOG_STATUSES).optional(),
  /** Relevance only means something with a search; without one it falls back to newest. */
  sort: z.enum(CATALOG_SORTS).default('relevance'),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
  limit: z.coerce.number().int().min(1).max(50).default(CATALOG_PAGE_SIZE),
});
export type CatalogQuery = z.infer<typeof catalogQuerySchema>;
